import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { showErrorToast, showSuccessToast } from 'components/Toast';
import PrivateRoute from 'components/Navigation/ProtectedRoute';
import OutlineButton from 'components/OutlineButton';
import PromoModalContent from './ActionModalContent';
import SolidButton from 'components/SolidButton';
import Modal from 'components/Modal';
import Table from 'components/Table';

import { IPromo } from 'utils/interfaces';
import { BUTTON_COLORS } from 'utils/enums';
import GeneralApi from 'utils/generalApi';
import { useAuth } from 'contexts/Auth';
import { formatDate } from 'utils/functions';

export default function PromosPage() {
  const history = useNavigate();
  const auth = useAuth();

  const generalApi = useMemo(() => new GeneralApi(auth, history), [auth, history]);

  const [refresh, setRefresh] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingId, setLoadingId] = useState('');
  const [promos, setPromos] = useState<Array<IPromo>>([]);

  const [isActionOpen, setIsActionOpen] = useState(false);
  const [actionData, setActionData] = useState<IPromo | undefined>(undefined);

  const togglePromo = async (promoId: string, isActive: boolean) => {
    setLoadingId(promoId);
    let result: any = await generalApi.post('/toggle_promo', {
      promoId,
    });
    setLoadingId('');
    if (!result.success) return showErrorToast(result.data.err);
    showSuccessToast(isActive ? 'Promoción deshabilitada' : 'Promoción habilitada');
    setRefresh(true);
  };

  useEffect(() => {
    const getPromos = async () => {
      setLoading(true);
      const result = await generalApi.get('/promos');
      if (!result.success) {
        showErrorToast(result.message);
        setLoading(false);
        return;
      }
      setPromos(result.data);
      setLoading(false);
    };
    if (refresh) {
      getPromos();
      setRefresh(false);
    }
  }, [generalApi, refresh]);

  return (
    <PrivateRoute>
      <div className="text-[#1B205A]">
        <Modal
          isOpen={isActionOpen}
          onClose={() => {
            setIsActionOpen(false);
            setActionData(undefined);
          }}
        >
          <PromoModalContent
            title={actionData ? 'Editar serie' : 'Crear serie'}
            editData={actionData}
            onClose={() => {
              setIsActionOpen(false);
              setActionData(undefined);
            }}
            onSuccess={() => {
              setRefresh(true);
              setIsActionOpen(false);
              setActionData(undefined);
            }}
          />
        </Modal>
        <div className="pb-8 2xl:px-24">
          <div className="mt-6 justify-between space-y-4 px-8 pb-4 sm:flex sm:space-y-0">
            <p className="text-2xl font-bold">Lista de Promociones</p>
            <div className="space-y-2 sm:ml-20 sm:flex sm:space-y-0 sm:space-x-4">
              <OutlineButton color={BUTTON_COLORS.BLUE} onClick={() => setIsActionOpen(true)}>
                Nueva Promoción
              </OutlineButton>
            </div>
          </div>
          <Table
            isLoading={loading}
            loadingText="Buscando promociones"
            columns={[
              {
                title: 'Nombre',
                dataKey: 'name',
              },
              {
                title: 'spCompra',
                dataKey: 'sp_compra',
              },
              {
                title: 'spVenta',
                dataKey: 'sp_venta',
              },
              {
                title: 'Límite - Persona',
                dataKey: 'per_person',
              },
              {
                title: 'Límite - Uso',
                dataKey: 'per_use',
              },
              {
                title: 'Fecha Inicio',
                dataKey: 'from',
                render: (item: IPromo) => formatDate(new Date(item.from), 'yyyy/MM/dd', false),
              },
              {
                title: 'Fecha Final',
                dataKey: 'to',
                render: (item: IPromo) => formatDate(new Date(item.to), 'yyyy/MM/dd', false),
              },
              {
                title: 'Opciones',
                dataKey: 'options',
                disableSort: true,
                render: (item: IPromo) => (
                  <div className="flex space-x-2 pr-2">
                    <SolidButton
                      color={BUTTON_COLORS.BLUE}
                      onClick={() => {
                        setIsActionOpen(true);
                        setActionData(item);
                      }}
                    >
                      Editar
                    </SolidButton>
                    <SolidButton
                      isLoading={loadingId === item._id}
                      color={item.is_active ? BUTTON_COLORS.RED : BUTTON_COLORS.BLUE}
                      onClick={() => togglePromo(item._id, item.is_active)}
                    >
                      {item.is_active ? 'Deshabilitar' : 'Habilitar'}
                    </SolidButton>
                  </div>
                ),
              },
            ]}
            data={promos}
          />
        </div>
      </div>
    </PrivateRoute>
  );
}
